import React from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { Button } from '../../../../interaxDesignSystemComponents'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { getOnboardingStatusColor, OnboardingStepStatus } from './onboardingStepStatus.config'

const toSxArray = (value: SxProps<Theme>) => (Array.isArray(value) ? value : [value])

/** Layer a caller override on top of the base card, whether it arrives as one sx object or an array. */
function mergeCardStyles(base: SxProps<Theme>, override?: SxProps<Theme>): SxProps<Theme> {
  if (!override) return base
  return [...toSxArray(base), ...toSxArray(override)] as SxProps<Theme>
}

export interface OnboardingStepCardV2Props {
  stepNumber: number
  title: string
  buttonText: string
  status: OnboardingStepStatus
  statusLabel: string
  icon: React.ReactNode
  onAction: () => void | Promise<void>
  onButtonAction?: () => void | Promise<void>
  className?: string
  sx?: SxProps<Theme>
}

/**
 * OnboardingStepCardV2 - New design for onboarding steps
 * 407x407 size with circular play button and modern design
 */
export const OnboardingStepCardV2: React.FC<OnboardingStepCardV2Props> = ({
  stepNumber,
  title,
  buttonText,
  status,
  statusLabel,
  icon,
  onAction,
  onButtonAction,
  className = '',
  sx
}) => {
  const { t } = useTranslation()

  const baseCardStyles: SxProps<Theme> = {
    width: '100%',
    maxWidth: '407px',
    height: '407px',
    borderRadius: '16px',
    backgroundColor: INTERAX_COLORS.heritageBlue40,
    border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
    padding: '24px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    cursor: 'pointer',
    transition: 'background 0.2s ease, border 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
    outline: 'none',
    '&:hover': {
      transform: 'translateY(-0px)',
      border: `1px solid ${INTERAX_COLORS.sun}`,
      background:
        'linear-gradient(135.41deg, rgba(255, 221, 179, 0.4) 0%, rgba(255, 171, 64, 0) 106.82%), rgba(46, 79, 95, 0.4)',
      boxShadow: '0 12px 36px rgba(0, 0, 0, 0.35)'
    },
    '&:focus-visible': {
      border: `1px solid ${INTERAX_COLORS.sun}`,
      boxShadow: `0 0 0 4px ${INTERAX_COLORS.sun}33`
    }
  }

  const statusColor = getOnboardingStatusColor(status)

  return (
    <Box
      role="button"
      tabIndex={0}
      className={className}
      onClick={onAction}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onAction()
        }
      }}
      sx={mergeCardStyles(baseCardStyles, sx)}
    >
      {/* Header Section - Step Number and Status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 2
        }}
      >
        {/* Step Number */}
        <Box
          component="span"
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: INTERAX_COLORS.white,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {t('gamer.onboarding.stepLabel', { number: stepNumber })}
        </Box>

        {/* Status Badge — never wraps; stays a single pill on one line */}
        <Box
          component="span"
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: statusColor,
            px: 1.5,
            py: 0.5,
            borderRadius: '12px',
            backgroundColor: `${statusColor}20`,
            border: `1px solid ${statusColor}40`,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
        >
          {statusLabel}
        </Box>
      </Box>

      {/* Title */}
      <Box
        component="h3"
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          mb: 2,
          lineHeight: 1.2
        }}
      >
        {title}
      </Box>

      {/* Central Icon/Play Button Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          maxHeight: '200px',
          minHeight: '120px',
          overflow: 'hidden',
          mb: 2
        }}
      >
        {icon}
      </Box>

      {/* Action Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start'
        }}
      >
        <Button
          variant="secondary"
          onClick={(event) => {
            if (event) {
              event.stopPropagation()
            }
            ;(onButtonAction ?? onAction)()
          }}
          className="min-w-[140px] h-10 text-sm font-semibold"
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  )
}
